import { formatToCostString } from "../commonfunctions/cost-calculation";
import { subdomainTypes } from "../context/availableSubdomains";
import { OrderDetail, OrderSplitCurrentOrder } from "../interfaces/interfaces";
import { PaymentOption } from "../pages/check-out/payment-selection/sendPaymentSelection";

export enum PaymentDetailsId {
  totalProducts = "totalProducts",
  shippingCost = "shippingCost",
  tax = "Tax",
  subTotal = "subTotal",
  subTotalOnline = "subTotalOnline",
  transactionFee = "transactionFee",
  transactionFeeNZ = "transactionFeeNZ",
  grandTotal = "grandTotal",
  discount = "discount",
}

export interface IDetail {
  id: PaymentDetailsId;
  title: string;
  value: string;
  boldTitle?: boolean;
}

const items = {
  [PaymentDetailsId.totalProducts]: {
    id: PaymentDetailsId.totalProducts,
    title: "Total Products",
    keyObject: "total_products",
  },
  totalProductsNZ: {
    id: PaymentDetailsId.totalProducts,
    title: "Items(s) Subtotal",
    keyObject: "total_products",
  },
  totalProductsAU: {
    id: PaymentDetailsId.totalProducts,
    title: "Total products",
    keyObject: "total_products",
  },
  shippingCostNZ: {
    id: PaymentDetailsId.shippingCost,
    title: "Delivery",
    keyObject: "shipping_cost",
  },
  [PaymentDetailsId.shippingCost]: {
    id: PaymentDetailsId.shippingCost,
    title: "Shipping Cost",
    keyObject: "shipping_cost",
  },
  taxNZ: {
    id: PaymentDetailsId.tax,
    title: "GST",
    keyObject: "tax",
  },
  [PaymentDetailsId.tax]: {
    id: PaymentDetailsId.tax,
    title: "Tax",
    keyObject: "tax",
  },
  [PaymentDetailsId.subTotal]: {
    id: PaymentDetailsId.subTotal,
    title: "Subtotal",
    keyObject: "subtotal",
    boldTitle: true,
  },
  [PaymentDetailsId.subTotalOnline]: {
    id: PaymentDetailsId.subTotalOnline,
    title: "Subtotal (excl. Merchant Service Fee)",
    keyObject: "subtotal",
    boldTitle: true,
  },
  [PaymentDetailsId.transactionFee]: {
    id: PaymentDetailsId.transactionFee,
    title: "Transaction Fee",
    keyObject: "transaction_fee",
  },
  [PaymentDetailsId.transactionFeeNZ]: {
    id: PaymentDetailsId.transactionFeeNZ,
    title: "Merchant Service Fee (MSF)",
    keyObject: "transaction_fee",
  },
  [PaymentDetailsId.grandTotal]: {
    id: PaymentDetailsId.grandTotal,
    title: "Grand total",
    keyObject: "grand_total",
    boldTitle: true,
  },
  [PaymentDetailsId.discount]: {
    id: PaymentDetailsId.grandTotal,
    title: "Discount",
    keyObject: "discount",
  },
};

const defaultItems = {
  nz: [items.totalProductsNZ, items.shippingCostNZ, items.taxNZ],
  au: [items.totalProductsAU, items.shippingCost],
  ca: [items.totalProducts, items.shippingCost, items.Tax],
};

const onlineItems = {
  nz: [items.subTotalOnline],
  au: [items.Tax, items.grandTotal],
  ca: [items.discount, items.grandTotal],
};

const onlineItemsPaid = {
  nz: [items.subTotal, items.transactionFeeNZ, items.grandTotal],
  au: [items.transactionFee, items.Tax, items.grandTotal],
  ca: [items.discount, items.grandTotal],
};

const bankTransferItems = {
  nz: [items.grandTotal],
  au: [items.Tax, items.grandTotal],
  ca: [items.discount, items.grandTotal],
};

// MAIN FUNCTION
export const getPaymentDetails = (
  order: OrderDetail | OrderSplitCurrentOrder,
  branch: subdomainTypes | "",
): IDetail[] => {
  if (branch === "") return [];

  type T = keyof typeof defaultItems;
  const defaultDetails = defaultItems[branch as T];
  const customDetails =
    order.payment_method === PaymentOption.payOnline
      ? onlineItems[branch as T]
      : bankTransferItems[branch as T];

  const details: IDetail[] = [...defaultDetails, ...customDetails].map((detail) => {
    type T = keyof typeof order.payment_details;
    const value = `${order.payment_details[detail.keyObject as T]}`;

    return {
      ...detail,
      value: formatToCostString(value),
    };
  });
  return details;
};

// SECOND FUNCTION
export const getPaymentDetailsPaid = (
  order: OrderDetail,
  branch: subdomainTypes | "",
): IDetail[] => {
  if (branch === "") {
    return [];
  }
  type T = keyof typeof defaultItems;
  const defaultDetails = defaultItems[branch as T];
  const customDetails =
    order.payment_method === PaymentOption.payOnline
      ? onlineItemsPaid[branch as T]
      : bankTransferItems[branch as T];

  // Orders charged before the AU surcharge removal still include the fee in grand_total,
  // so the row is kept for them and dropped when no fee was charged.
  const hasTransactionFee = Number(order.payment_details_paid.transaction_fee) > 0;

  const details: IDetail[] = [...defaultDetails, ...customDetails]
    .filter((detail) => detail.id !== PaymentDetailsId.transactionFee || hasTransactionFee)
    .map((detail) => {
      type T = keyof typeof order.payment_details_paid;
      const value = `${order.payment_details_paid[detail.keyObject as T]}`;

      return {
        ...detail,
        value: formatToCostString(value),
      };
    });
  return details;
};
